/**
 * Custom color picker with HSV selection and saved colors
 *
 * Uses HSV (Hue-Saturation-Value) for the 2D picker which is more intuitive:
 * - X axis: Saturation (0-100%)
 * - Y axis: Value/Brightness (100% at top, 0% at bottom)
 * - Hue bar below for selecting hue (0-360)
 */

import { useState, useRef, useCallback, useEffect } from 'react';

interface ColorPickerProps {
  value: string; // hex color
  onChange: (hex: string) => void;
  savedColors: string[];
  onSaveColor: (hex: string) => void;
}

// Convert HSV to hex
function hsvToHex(h: number, s: number, v: number): string {
  s /= 100;
  v /= 100;

  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };

  const r = Math.round(f(5) * 255);
  const g = Math.round(f(3) * 255);
  const b = Math.round(f(1) * 255);

  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

// Convert hex to HSV
function hexToHsv(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [0, 100, 100];

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : (d / max) * 100;
  const v = max * 100;

  if (d !== 0) {
    switch (max) {
      case r: h = 60 * (((g - b) / d) % 6); break;
      case g: h = 60 * ((b - r) / d + 2); break;
      case b: h = 60 * ((r - g) / d + 4); break;
    }
  }
  if (h < 0) h += 360;

  return [Math.round(h), Math.round(s), Math.round(v)];
}

export function ColorPicker({ value, onChange, savedColors, onSaveColor }: ColorPickerProps) {
  const [hsv, setHsv] = useState<[number, number, number]>(() => hexToHsv(value));
  const [hue, sat, val] = hsv;

  const svSquareRef = useRef<HTMLDivElement>(null);
  const hueBarRef = useRef<HTMLDivElement>(null);
  const isDraggingSV = useRef(false);
  const isDraggingHue = useRef(false);

  // Update HSV when external value changes significantly
  useEffect(() => {
    const newHsv = hexToHsv(value);
    const [newH, newS, newV] = newHsv;
    // Only update if significantly different to avoid feedback loops
    if (Math.abs(newH - hue) > 3 || Math.abs(newS - sat) > 3 || Math.abs(newV - val) > 3) {
      setHsv(newHsv);
    }
  }, [value]);

  const updateFromSV = useCallback((s: number, v: number) => {
    const newHsv: [number, number, number] = [hue, s, v];
    setHsv(newHsv);
    onChange(hsvToHex(hue, s, v));
  }, [hue, onChange]);

  const updateFromHue = useCallback((h: number) => {
    const newHsv: [number, number, number] = [h, sat, val];
    setHsv(newHsv);
    onChange(hsvToHex(h, sat, val));
  }, [sat, val, onChange]);

  // Handle saturation/value square interaction
  const handleSVInteraction = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!svSquareRef.current) return;
    const rect = svSquareRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    // x = saturation (0-100), y = value (100 at top, 0 at bottom)
    updateFromSV(Math.round(x * 100), Math.round((1 - y) * 100));
  }, [updateFromSV]);

  // Handle hue bar interaction
  const handleHueInteraction = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!hueBarRef.current) return;
    const rect = hueBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    updateFromHue(Math.round(x * 360));
  }, [updateFromHue]);

  // Mouse event handlers for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingSV.current) handleSVInteraction(e);
      if (isDraggingHue.current) handleHueInteraction(e);
    };
    const handleMouseUp = () => {
      isDraggingSV.current = false;
      isDraggingHue.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleSVInteraction, handleHueInteraction]);

  const currentHex = hsvToHex(hue, sat, val);

  return (
    <div style={{ width: 200 }}>
      {/* Saturation/Value square */}
      <div
        ref={svSquareRef}
        onMouseDown={(e) => {
          e.preventDefault();
          isDraggingSV.current = true;
          handleSVInteraction(e);
        }}
        style={{
          width: '100%',
          height: 120,
          borderRadius: 4,
          cursor: 'crosshair',
          position: 'relative',
          background: `
            linear-gradient(to top, #000, transparent),
            linear-gradient(to right, #fff, hsl(${hue}, 100%, 50%))
          `
        }}
      >
        {/* Picker indicator */}
        <div style={{
          position: 'absolute',
          left: `${sat}%`,
          top: `${100 - val}%`,
          width: 12,
          height: 12,
          border: '2px solid white',
          borderRadius: '50%',
          boxShadow: '0 0 2px rgba(0,0,0,0.8), inset 0 0 2px rgba(0,0,0,0.3)',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none'
        }} />
      </div>

      {/* Hue bar */}
      <div
        ref={hueBarRef}
        onMouseDown={(e) => {
          e.preventDefault();
          isDraggingHue.current = true;
          handleHueInteraction(e);
        }}
        style={{
          width: '100%',
          height: 14,
          marginTop: 8,
          borderRadius: 4,
          cursor: 'crosshair',
          position: 'relative',
          background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)'
        }}
      >
        {/* Hue indicator */}
        <div style={{
          position: 'absolute',
          left: `${(hue / 360) * 100}%`,
          top: '50%',
          width: 4,
          height: 12,
          background: 'white',
          border: '1px solid #333',
          borderRadius: 2,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none'
        }} />
      </div>

      {/* Current color preview and hex input */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <div style={{
          width: 28,
          height: 22,
          backgroundColor: currentHex,
          border: '1px solid #444',
          borderRadius: 3,
          flexShrink: 0
        }} />
        <input
          type="text"
          value={currentHex}
          onChange={(e) => {
            const hex = e.target.value;
            if (/^#[0-9a-fA-F]{6}$/.test(hex)) {
              setHsv(hexToHsv(hex));
              onChange(hex);
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            background: '#374151',
            border: '1px solid #4b5563',
            borderRadius: 3,
            padding: '3px 6px',
            color: 'white',
            fontFamily: 'monospace',
            fontSize: 11
          }}
        />
        <button
          onClick={() => onSaveColor(currentHex)}
          style={{
            background: '#374151',
            border: '1px solid #4b5563',
            borderRadius: 3,
            padding: '3px 8px',
            color: '#9ca3af',
            fontSize: 12,
            cursor: 'pointer',
            flexShrink: 0
          }}
          title="Save to palette"
        >
          +
        </button>
      </div>

      {/* Saved colors */}
      {savedColors.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{
            fontSize: 9,
            color: '#6b7280',
            marginBottom: 4,
            textTransform: 'uppercase'
          }}>
            Saved
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {savedColors.map((color, i) => (
              <div
                key={i}
                onClick={() => {
                  setHsv(hexToHsv(color));
                  onChange(color);
                }}
                style={{
                  width: 18,
                  height: 18,
                  backgroundColor: color,
                  border: color.toLowerCase() === currentHex.toLowerCase()
                    ? '2px solid white'
                    : '1px solid #444',
                  borderRadius: 3,
                  cursor: 'pointer'
                }}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
